<template>
    <div v-if="isGooglePayAvailable" @click="createTransaction">
        <div id="container"></div>
    </div>
</template>

<script>

import axios from "axios";
import { mapState, mapMutations, mapGetters } from "vuex";
import { sweetAlert2 } from "../../../mixins/sweet-alert2";
import { showBlocker } from "../../../mixins/show-blocker";
import { paymentMethods } from "../../../constants/payment-methods";
import { appSettings } from "../../../mixins/app-settings";
import { paymentProcessors } from "../../../constants/payment-processors";

const baseRequest = {
    apiVersion: 2,
    apiVersionMinor: 0
};

// These are initial values as per Google documentation.
const allowedCardNetworks = ["AMEX", "DISCOVER", "INTERAC", "JCB", "MASTERCARD", "VISA"];
const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];

const baseCardPaymentMethod = {
    type: "CARD",
    parameters: {
        allowedAuthMethods: allowedCardAuthMethods,
        allowedCardNetworks: allowedCardNetworks
    }
};

export default {
    name: "GooglePay",
    mixins: [sweetAlert2, showBlocker, appSettings],
    data() {
        return {
            isGooglePayInitialized: false,
            isGooglePayAvailable: true,
            merchantId: "",
            merchantName: "",
            gatewayName: "",
            getMerchantDetailsUrl: "",
        }
    },
    computed: {
        ...mapState({
            siteId: state => state.session.siteId,
            accountId: state => state.session.accountId,
            checkId: state => state.session.checkId,
            googlePayScriptLoaded: state => state.session.googlePayScriptLoaded,
            googlePaymentClient: state => state.session.googlePaymentClient,
            apiBaseUrl: state => state.session.apiBaseUrl
        }),
        ...mapGetters([
            "balanceWithTip"
        ]),
        isPayeezy() {
            return this.$store.state.session.paymentProcessor === paymentProcessors.payeezy;
        },
        isAuthorizeNet() {
            return this.$store.state.session.paymentProcessor === paymentProcessors.authorizeNet;
        }
    },
    methods: {
        ...mapMutations([
            "setGooglePayScriptLoaded",
            "setGooglePaymentClient",
            "setLoadingStatus",
            "setPayment"
        ]),
        async loadGooglePayScriptAndInitializeClient() {
            if (!this.googlePayScriptLoaded) {
                this.$loadScript("https://pay.google.com/gp/p/js/pay.js")
                    .then(async () => {
                        this.setGooglePayScriptLoaded(true);
                        await this.createGooglePayClient();
                    })
                    .catch(() => {
                        // If we have trouble loading the Google Pay script, we disable this
                        // form of payment.
                        this.isGooglePayInitialized = true;
                        this.isGooglePayAvailable = false;
                        console.error("Unable to load Google Pay script.");
                    })
            }
            else {
                await this.createGooglePayClient();
            }
        },
        async createGooglePayClient() {
            // Get Google Pay environment
            let googlePayEnvironment = await axios.get(this.getApiBaseUrl() + "/payment/googlePayEnvironment/" + this.siteId + "/" + this.accountId)
                .then(response => { return response.data })
                .catch(error => {
                    console.error(error);
                    this.isGooglePayInitialized = true;
                })

            if (googlePayEnvironment !== "TEST" && googlePayEnvironment !== "PRODUCTION") {
                console.error("Invalid Google Pay Environment: " + googlePayEnvironment);
                this.$router.push({ name: "Error", params: { errorCode: "1021" } });
                return;
            }

            // Initialize Google payment client.
            this.setGooglePaymentClient(new window.google.payments.api.PaymentsClient({
                environment: googlePayEnvironment
            }));
            
            // create google pay button
            const container = document.getElementById('container');
            const button = this.googlePaymentClient.createButton({
                buttonColor: 'default',
                buttonType: 'plain',
                buttonRadius: 4,
                allowedPaymentMethods: [baseCardPaymentMethod]
            });

            container.appendChild(button);

            this.checkIfReadyToPay();
        },
        async checkIfReadyToPay() {

            const isReadyToPayRequest = Object.assign({}, baseRequest);
            isReadyToPayRequest.allowedPaymentMethods = [baseCardPaymentMethod];

            const vm = this;

            this.googlePaymentClient.isReadyToPay(isReadyToPayRequest)
                .then(function (response) {
                    if (response.result) {
                        vm.isGooglePayAvailable = true;

                    }
                })
                .catch(function (err) {
                    vm.isGooglePayAvailable = false;
                    console.error("Error determining readiness to use Google Pay: ", err);
                })
                .finally(() => {
                    vm.isGooglePayInitialized = true;
                });
        },
        async createTransaction() {
            // In the rare event that user triggers Google Pay clicked event before the
            // initialization is done, we have to ignore the event. User would have the
            // clue to tap the button again, and usually by then, the initialization
            // would be done. We cannot auto trigger a call to this function at a later
            // time with something like a setTimeout, because the pop up blocker would
            // not allow that.
            if (!this.isGooglePayInitialized) {
                return;
            }

            // If Google Pay is not supported for some reason, we will ask user to try another
            // payment method.
            if (!this.isGooglePayAvailable) {
                console.error("Google Pay failed to initialize but Google Pay payment triggered.");
                this.swalError("1015");
                return;
            }

            if (!this.isLoading) {
                this.showBlockerWithNoSpinner();
            }

            const tokenizationSpecification = {
                type: "PAYMENT_GATEWAY",
                parameters: {
                    "gateway": this.gatewayName,
                    "gatewayMerchantId": this.merchantId
                }
            };

            const cardPaymentMethod = Object.assign(
                { tokenizationSpecification: tokenizationSpecification },
                baseCardPaymentMethod
            );

            const paymentDataRequest = Object.assign({}, baseRequest);
            paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];
            paymentDataRequest.transactionInfo = {
                totalPriceStatus: "FINAL",
                totalPrice: this.balanceWithTip.toFixed(2).toString(),
                currencyCode: "USD",
                countryCode: "US"
            };
            paymentDataRequest.merchantInfo = {
                merchantName: this.merchantName,
                merchantId: this.merchantId
            };

            await this.processPayment(paymentDataRequest);
        },
        async processPayment(paymentDataRequest) {
            this.googlePaymentClient.loadPaymentData(paymentDataRequest)
                .then(paymentData => { return paymentData; })
                .then(async (result) => {
                    this.sendPayloadToServer(result);
                })
                .catch(error => {
                    this.stopProgressIndicator();
                    console.error(error);

                    // A "cancel" is considered an error. For anything other than a "cancel", it's
                    // an unknown error condition that we have to handle.
                    if (error !== undefined && error.statusCode !== "CANCELED") {
                        this.$router.push({ name: "Error", params: { errorCode: "1016" } });
                    }
                });
        },
        async sendPayloadToServer(payment) {
            // Google Pay is done at this point (pop up closed). We need to show the spinner.
            this.startProgressIndicator();

            let payload = {
                SiteId: this.siteId,
                AccountId: this.accountId,
                CheckId: this.checkId,
                PaymentAmount: this.balanceWithTip,
                googlePayPaymentData: payment.paymentMethodData.tokenizationData.token
            }

            await axios.post(this.getApiBaseUrl() + "/payment/payment", payload)
                .then(response => {
                    if (response.data) {
                        let payment = response.data;
                        payment.paymentName = paymentMethods.googlePay;
                        this.setPayment(payment);
                        this.$router.push({ name: "PaymentSummary" });
                    }
                    else {
                        this.$router.push({ name: "Error", params: { errorCode: "1000" } });
                    }
                })
                .catch(error => {
                    console.log("error: ", error);
                    if (error.response &&
                        error.response.data &&
                        error.response.data.errorCode) {
                        switch (error.response.data.errorCode) {
                            case "1008":
                            case "1009":
                                this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                                break;
                            default:
                                if (error.response.data.errorMessage) {
                                    console.error(error.response.data.errorMessage);
                                }
                                this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                        }
                    }
                    else {
                        console.error(error);
                        this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                    }
                })
                .finally(() => {
                    this.stopProgressIndicator();
                });
        },
        async initialize() {
            axios.get(this.getApiBaseUrl() + this.getMerchantDetailsUrl + this.siteId + "/" + this.accountId)
                .then(response => {
                    if (response.data) {
                        if (this.isPayeezy) {
                            this.merchantId = response.data.merchantId;
                        } else if (this.isAuthorizeNet) {
                            this.merchantId = response.data.gatewayId;
                        }

                        this.merchantName = response.data.merchantName;

                        if (this.merchantId !== "" && this.merchantName !== "") {
                            this.loadGooglePayScriptAndInitializeClient();
                        }
                        else {
                            console.log("Unable to initialize Google Pay - Missing merchant information.");
                            this.isGooglePayAvailable = false;
                        }
                    }
                })
                .catch(error => {
                    console.error("Error retrieving merchant information - " + error);
                    this.$router.push({ name: "Error", params: { errorCode: "1024" } });
                })
        }
    },
    async mounted() {
        if (this.isPayeezy) {
            this.gatewayName = "firstdata";
            this.getMerchantDetailsUrl = "/payeezy/merchantinformation/";
        } else if (this.isAuthorizeNet) {
            this.gatewayName = "authorizenet";
            this.getMerchantDetailsUrl = "/authorizenet/getmerchantdetails/";
        }

        await this.initialize();
    },
}
</script>